<template lang="pug">
main.volumealert-form-wrapper(:class="{'form-volumealert-builder': this.parent === 'builder'}")

    div.modal-body.modal-content-volume-alert

        div.subheader.mg-tp-24.mg-bt-16(v-if="this.parent === 'builder'")
            div.subheader-info Set volume alert to receive an email when daily volume reach a limit.

        //- email
        div.form-subsection
            div.form-label__required
                label Email
            input.form-input(
                id="email_audience_limit"
                name="email"
                placeholder="user@weborama.com"
                v-model="volumeAlertForm.email_audience_limit"
                v-validate="'max:255|required|email'"
                @blur="handleResetFieldOnLeave('email')"
                type="text"
            )
            span.form-validate-error {{ errors.first('email') }}

        //- cc
        div.form-subsection
            div.form-label.l-flex-row--between
                label Emails Cc
                div.counter {{ volumeAlertForm.email_cc_audience_limit.length }}/10
            EmailCcField(
                parent="FormVolumeAlert"
                placeholder="cc@weborama.com"
                :maxItems="10"
                @formCcListUpdate="updateFormCcList"
                :defaultValues="defaultEmailCcs"
            )

        //- Below
        div.form-subsection(:class="{'ui-disabled': disableBelowLimitField}")
            div.form-label.l-flex-row--between.l-align-center
                label Below
                span.form-required-field(v-if="!isBelowLimit && !cmptCheckAbove") required
                svg-icon.form-ok-field(
                    v-if="cmptCheckBelow"
                    name='check' size="small")
            input.form-input(
                id="below_limit"
                name="below_limit"
                placeholder="Below"
                v-model="volumeAlertForm.below_limit"
                v-validate="vvalidateBelowLimit"
                type="text"
                :maxlength="maxLimit.toString().length"
            )
            span.form-validate-error {{ errors.first('below_limit') }}

        //- Above
        div.form-subsection
            div.form-label.l-flex-row--between.l-align-center
                label.form-label Above
                span.form-required-field(v-if="!isAboveLimit && !cmptCheckBelow") required
                svg-icon.form-ok-field(
                    v-if="cmptCheckAbove"
                    name='check' size="small")
            input.form-input(
                id="above_limit"
                name="above_limit"
                placeholder="Above"
                v-model="volumeAlertForm.above_limit"
                v-validate="vvalidateAboveLimit"
                type="text"
                :maxlength="maxLimit.toString().length"
            )
            span.form-validate-error {{ errors.first('above_limit') }}

    //- Footer
    footer.modal-footer
        div.footer-content.l-flex-row--between
            button.btn.btn--medium.btn--round(@click="closeFormVolumeAlert()") Cancel
            button.btn.btn--medium.btn--round.btn-rev(
                :class="{'ui-disabled': disabledSaveButton}"
                @click="saveVolumeAlert()") {{ submitButtonText }}
</template>

<script>
// Avoid repeating namespace for each store action/mutation/getter/state
import Vue from 'vue'
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import { getTokenUserEmail } from '@/utils/helpers'
import { toNumber, cloneDeep, size, isNil, isEmpty, join, isEqual } from 'lodash'
import EmailCcField from '@/components/common/EmailCcField'

// Form Validation

export default {
    name: 'FormVolumeAlert',
    components: {
        EmailCcField
    },
    props: {
        parent: { type: String, required: true }
    },

    data() {
        return {
            volumeAlertForm: {
                email_audience_limit: '',
                email_cc_audience_limit: [],
                above_limit: '',
                below_limit: ''
            },
            maxLimit: 2147483647,
            inputCcValue: '',
            processing: false,
            defaultEmailCcs: []
        }
    },

    created() {
        this.isEdit ? this.setEditVolumeAlertData() : this.setDefaultVolumeAlertData()
        const dict = {
            custom: {
                below_limit: {
                    max_value: () => {
                        return isEmpty(this.volumeAlertForm.above_limit) &&
                            toNumber(this.volumeAlertForm.below_limit) > toNumber(this.volumeAlertForm.above_limit)
                            ? 'The value can not be greater than ' + this.maxLimit
                            : 'The value must be inferior to the "Above" value'
                    },
                    numeric: 'The value must be a positive integer',
                    min_value: 'The value must be 1 or more'
                },
                above_limit: {
                    min_value: () => {
                        return isEmpty(this.volumeAlertForm.below_limit) ? 'The value must be 1 or more' : 'The value must be superior to the “Below” value'
                    },
                    numeric: 'The value must be a positive integer',
                    max_value: 'The value can not be greater than ' + this.maxLimit
                }
            }
        }
        this.$validator.localize('en', dict)
    },

    computed: {
        // Call mapState
        ...mapState('ui/monitoring', {
            selectedAudienceId: state => state.selectedRowId
        }),
        ...mapState('ui/volumeAlert', {
            isEdit: state => state.isEdit,
            storeVolumeAlertForm: state => state.form
        }),
        ...mapGetters('data/audience', ['getItemById']),

        audienceInfo() {
            const _info = this.getItemById({ id: this.selectedAudienceId })
            return _info == false ? null : _info
        },

        volumeAlertDetails() {
            return this.audienceInfo ? this.audienceInfo.volumeAlert.details : {}
        },

        // Computed functions
        isCcList() {
            const arrayCc = this.inputCcValue.split(/[\s,;\t\n]+/)
            return size(arrayCc) !== 1
        },
        ccInputDisabled() {
            return size(this.volumeAlertForm.email_cc_audience_limit) > 9
        },
        isBelowLimit() {
            return !isNil(this.volumeAlertForm.below_limit) && this.volumeAlertForm.below_limit !== ''
        },
        isAboveLimit() {
            return !isNil(this.volumeAlertForm.above_limit) && this.volumeAlertForm.above_limit !== ''
        },
        disableBelowLimitField() {
            return (
                (this.volumeAlertForm.above_limit === 0 || this.volumeAlertForm.above_limit === 1) &&
                (this.volumeAlertForm.below_limit === '' || isNil(this.volumeAlertForm.below_limit))
            )
        },

        vvalidateBelowLimit() {
            const minBelowLimit = toNumber(toNumber(this.volumeAlertForm.above_limit) - 1)
            const belowLimit = toNumber(this.volumeAlertForm.below_limit)
            if (belowLimit === 0 || belowLimit === 1) return 'numeric|min_value:1'
            return this.isAboveLimit ? 'numeric|min_value:1|max_value:' + minBelowLimit : 'numeric|min_value:1|max_value:' + this.maxLimit + '|required'
        },

        vvalidateAboveLimit() {
            const minAboveLimit = this.volumeAlertForm.below_limit !== '' ? toNumber(toNumber(this.volumeAlertForm.below_limit) + 1) : 1
            return this.volumeAlertForm.above_limit >= this.maxLimit ? 'numeric|min_value:1|max_value:' + this.maxLimit : 'numeric|min_value:' + minAboveLimit
        },

        disabledSaveButton() {
            return (
                this.errors.has('email') ||
                this.errors.has('above_limit') ||
                this.errors.has('below_limit') ||
                (this.volumeAlertForm.above_limit === '' && this.volumeAlertForm.below_limit === '') ||
                this.hasNotBeenEdited
            )
        },

        hasNotBeenEdited() {
            if (!this.isEdit) return false
            const email_cc_audience_limitExist = this.volumeAlertDetails?.email_cc_audience_limit && this.volumeAlertDetails?.email_cc_audience_limit !== ''
            const emailCcAudience = !email_cc_audience_limitExist ? [] : this.volumeAlertDetails?.email_cc_audience_limit.split(',')
            return (
                this.volumeAlertDetails?.above_limit === this.volumeAlertForm?.above_limit &&
                this.volumeAlertDetails?.below_limit === this.volumeAlertForm?.below_limit &&
                this.volumeAlertDetails?.email_audience_limit === this.volumeAlertForm?.email_audience_limit &&
                isEqual(emailCcAudience, this.volumeAlertForm?.email_cc_audience_limit)
            )
        },

        submitButtonText() {
            if (this.parent === 'monitoring') {
                return (!isEmpty(this.volumeAlertDetails) ? 'Edit' : 'Create') + ' volume alert'
            } else {
                return (!isEmpty(this.storeVolumeAlertForm) ? 'Edit' : 'Create') + ' volume alert'
            }
        },

        cmptCheckBelow() {
            return this.isBelowLimit && this.volumeAlertForm.below_limit !== '' && !this.errors.first('below_limit')
        },
        cmptCheckAbove() {
            return this.isAboveLimit && this.volumeAlertForm.above_limit !== '' && !this.errors.first('above_limit')
        }
    },

    methods: {
        ...mapActions('data/audience', ['editVolumeAlert', 'createVolumeAlert']),

        // Call mapMutations
        ...mapMutations('ui/volumeAlert', ['setVolumeAlertForm', 'setDefaultVolumeAlertForm']),
        ...mapMutations('data/audience', ['setAudienceVolumeAlert']),
        ...mapMutations('ui/builder', ['setActiveTabFormAudience']),
        ...mapMutations('ui/monitoring', ['setActiveTab']),

        // Methods
        setDefaultVolumeAlertData() {
            this.volumeAlertForm = {
                email_audience_limit: getTokenUserEmail(Vue.prototype.$keycloak),
                email_cc_audience_limit: [],
                above_limit: '',
                below_limit: ''
            }
        },
        setEditVolumeAlertData() {
            const volumeAlertFormInStore = this.parent === 'monitoring' ? cloneDeep(this.volumeAlertDetails) : cloneDeep(this.storeVolumeAlertForm)

            let email_cc_audience_limit
            if (
                volumeAlertFormInStore.email_cc_audience_limit === '[]' ||
                volumeAlertFormInStore.email_cc_audience_limit === '' ||
                isEqual(volumeAlertFormInStore.email_cc_audience_limit, [])
            ) {
                email_cc_audience_limit = []
            } else if (typeof volumeAlertFormInStore.email_cc_audience_limit === 'object') {
                email_cc_audience_limit = volumeAlertFormInStore.email_cc_audience_limit
                this.defaultEmailCcs = volumeAlertFormInStore.email_cc_audience_limit
            } else if (typeof volumeAlertFormInStore.email_cc_audience_limit === 'string') {
                email_cc_audience_limit = volumeAlertFormInStore.email_cc_audience_limit.split(',')
                this.defaultEmailCcs = volumeAlertFormInStore.email_cc_audience_limit.split(',')
            }

            this.volumeAlertForm = {
                email_audience_limit: volumeAlertFormInStore.email_audience_limit,
                email_cc_audience_limit,
                above_limit: volumeAlertFormInStore.above_limit,
                below_limit: volumeAlertFormInStore.below_limit
            }
        },
        closeFormVolumeAlert() {
            if (this.parent === 'builder') {
                this.setActiveTabFormAudience({ tab: 'audienceDetails' })
            }
            if (this.parent === 'monitoring') {
                this.setDefaultVolumeAlertForm()
            }
            this.processing = false
        },
        updateFormCcList(ccList) {
            this.volumeAlertForm.email_cc_audience_limit = ccList
        },
        saveVolumeAlert() {
            if (this.processing) return // Prevent multiple creation
            this.processing = true
            this.$validator.validateAll().then(isValid => {
                if (isValid) {
                    if (this.parent === 'monitoring') {
                        if (this.isEdit) {
                            if (!this.isBelowLimit) this.volumeAlertForm.below_limit = ''
                            if (!this.isAboveLimit) this.volumeAlertForm.above_limit = ''
                            this.editVolumeAlert({
                                audienceId: this.selectedAudienceId,
                                volumeAlertParams: {
                                    ...this.volumeAlertForm,
                                    id: this.volumeAlertDetails.id,
                                    email_cc_audience_limit: join(this.volumeAlertForm.email_cc_audience_limit, ',')
                                }
                            })
                        } else {
                            if (!this.isBelowLimit) delete this.volumeAlertForm.below_limit
                            if (!this.isAboveLimit) delete this.volumeAlertForm.above_limit
                            this.createVolumeAlert({
                                ...this.volumeAlertForm,
                                audience_id: this.selectedAudienceId,
                                email_cc_audience_limit: join(this.volumeAlertForm.email_cc_audience_limit, ',')
                            })
                        }
                        this.setActiveTab({ tab: 'information' })
                        this.setAudienceVolumeAlert({ audienceId: this.selectedAudienceId, volumeAlert: { isVisibleInMonitoringPanel: true } })
                    } else if (this.parent === 'builder') {
                        this.setVolumeAlertForm({ form: this.volumeAlertForm })
                        this.closeFormVolumeAlert()
                    }
                }
            })
        },
        handleResetFieldOnLeave(field) {
            if (field === 'email' && this.errors.has('email'))
                this.volumeAlertForm.email_audience_limit = getTokenUserEmail(Vue.prototype.$keycloak)
        }
    }
}
</script>
